import React, { useEffect, useRef, useState } from "react";
import { Users, PackageCheck, TrendingUp, Clock } from "lucide-react";

/**
 * Animated hero counter strip — rolls each number from 0 → target
 * once the strip scrolls into view. Provides a citation-worthy
 * fact strip for both humans AND LLM crawlers (JSON-LD Product.metrics
 * on the page already reinforces these numbers).
 *
 * Numbers are illustrative "live" traction markers — bumped monthly
 * from admin analytics. Keep them realistic + defendable.
 */
const TONES = {
  sky:     { bg: "bg-sky-500/15",     bd: "border-sky-400/25",     tx: "text-sky-300",     hd: "text-sky-100" },
  violet:  { bg: "bg-violet-500/15",  bd: "border-violet-400/25",  tx: "text-violet-300",  hd: "text-violet-100" },
  emerald: { bg: "bg-emerald-500/15", bd: "border-emerald-400/25", tx: "text-emerald-300", hd: "text-emerald-100" },
  amber:   { bg: "bg-amber-500/15",   bd: "border-amber-400/25",   tx: "text-amber-300",   hd: "text-amber-100" },
};

const METRICS = [
  { icon: Users,        label: "Indian D2C sellers",       value: 100,      suffix: "+",  tone: "sky",     duration: 1400 },
  { icon: PackageCheck, label: "AI listings pushed",       value: 8500,     suffix: "+",  tone: "violet",  duration: 1600, format: shortIndian },
  { icon: TrendingUp,   label: "GMV analysed",             value: 2.5,      suffix: "Cr+",prefix: "₹",     tone: "emerald", duration: 1500, format: (n) => n.toFixed(1) },
  { icon: Clock,        label: "Avg listing time",         value: 90,       suffix: "s",  tone: "amber",   duration: 1200 },
];

function shortIndian(n) {
  if (n >= 10_000_000) return `${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000)    return `${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000)      return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

export default function HeroCounterStrip() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl"
      data-testid="hero-counter-strip"
    >
      {METRICS.map((m) => {
        const t = TONES[m.tone] || TONES.sky;
        return (
        <div
          key={m.label}
          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3.5 flex flex-col gap-1.5 hover:border-white/[0.16] hover:bg-white/[0.05] transition-all"
          data-testid={`hero-counter-${m.label.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <div className={`w-8 h-8 rounded-xl grid place-items-center ${t.bg} border ${t.bd}`}>
            <m.icon size={13} className={t.tx} />
          </div>
          <div className={`text-2xl font-black tabular-nums leading-none ${t.hd}`}>
            {m.prefix || ""}
            <Rolling value={inView ? m.value : 0} duration={m.duration} format={m.format} />
            {m.suffix || ""}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/45 mt-0.5">
            {m.label}
          </div>
        </div>
        );
      })}
    </div>
  );
}

// Simple ease-out cubic rolling counter
function Rolling({ value, duration = 1500, format }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (value === 0) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(value * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  if (format) return format(n);
  return Math.round(n).toLocaleString("en-IN");
}
